import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SESSION_FLAG = "er_cmdk_session_seen";
const SHOW_DELAY_MS = 800;
const AUTO_DISMISS_MS = 5000;

// Phase 12.1 — Cmd+K hint pill
// Shows once per fresh session for users who haven't permanently dismissed it.
// Listens for `cmdk:opened` (dispatched by CommandPalette) to record persistent dismissal.
export default function CmdKFirstSessionHint() {
  const { user, setUser } = useAuth();
  const [visible, setVisible] = useState(false);
  const dismissedRef = useRef(false);
  const persistedRef = useRef(false);
  const timerRef = useRef(null);

  const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPod|iPhone|iPad/.test(navigator.platform);

  // Persistently dismiss (write to /api/auth/profile + flip session flag).
  const persistDismiss = async () => {
    if (persistedRef.current) return;
    persistedRef.current = true;
    try {
      const { data } = await api.patch("/auth/profile", {
        cmdk_dismissed: true,
      });
      if (data && setUser) {
        setUser({ ...data });
      }
    } catch {
      // Best-effort — sessionStorage still prevents re-showing this session.
    }
  };

  const dismiss = ({ persist }) => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    setVisible(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    try {
      sessionStorage.setItem(SESSION_FLAG, "1");
    } catch {
      /* ignore */
    }
    if (persist) {
      persistDismiss();
    }
  };

  useEffect(() => {
    if (!user) return undefined;
    if (user.cmdk_dismissed) return undefined;

    let alreadySeenThisSession = false;
    try {
      alreadySeenThisSession = sessionStorage.getItem(SESSION_FLAG) === "1";
    } catch {
      /* ignore */
    }
    if (alreadySeenThisSession) return undefined;

    // Slide in after a small delay so it doesn't fight the page-enter motion.
    const showTimer = setTimeout(() => {
      if (!dismissedRef.current) {
        setVisible(true);
        // Auto-dismiss timer.
        timerRef.current = setTimeout(() => {
          dismiss({ persist: false });
        }, AUTO_DISMISS_MS);
      }
    }, SHOW_DELAY_MS);

    // The user actually opened the palette → permanent dismissal.
    // Call persistDismiss directly (it has its own guard) so we don't depend
    // on dismiss()'s dismissedRef state, which may race with the global
    // mousedown / auto-dismiss timer.
    const onCmdkOpened = () => {
      try {
        sessionStorage.setItem(SESSION_FLAG, "1");
      } catch {
        /* ignore */
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      dismissedRef.current = true;
      setVisible(false);
      persistDismiss();
    };
    window.addEventListener("cmdk:opened", onCmdkOpened);

    // Any global click or non-Cmd+K keypress just hides for this session.
    // (We do NOT match Cmd+K here — that fires `cmdk:opened` and we want the
    // permanent-dismissal handler to win.)
    const onKey = (e) => {
      const isCmdK =
        (e.metaKey || e.ctrlKey) && (e.key || "").toLowerCase() === "k";
      if (isCmdK) return;
      if (visible || timerRef.current) {
        dismiss({ persist: false });
      }
    };
    const onMouse = () => {
      if (visible || timerRef.current) {
        dismiss({ persist: false });
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onMouse);

    return () => {
      clearTimeout(showTimer);
      if (timerRef.current) clearTimeout(timerRef.current);
      window.removeEventListener("cmdk:opened", onCmdkOpened);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onMouse);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="cmdk-first-session-hint"
          initial={{ opacity: 0, y: -4, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -4, scale: 0.97 }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          className="pointer-events-auto"
          role="status"
          aria-live="polite"
        >
          <div
            className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[11px]"
            style={{
              backgroundColor: "var(--surface)",
              border: "1px solid var(--border-strong)",
              boxShadow: "var(--shadow-modal)",
              color: "var(--page-fg-secondary)",
            }}
          >
            <span aria-hidden className="hidden sm:inline">
              Press
            </span>
            <span className="kbd">{isMac ? "⌘" : "Ctrl"}</span>
            <span className="kbd">K</span>
            <span className="text-[10px] uppercase tracking-[0.10em]">
              to search
            </span>
            <button
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                dismiss({ persist: true });
              }}
              data-testid="cmdk-hint-dismiss"
              aria-label="Dismiss hint"
              className="ml-1 rounded text-[14px] leading-none transition-opacity hover:opacity-80"
              style={{ color: "var(--page-fg-muted)" }}
            >
              ×
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
